import { Divider, Headline, Icon, Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import {
	RateSectionContainerStyles,
	SubtotalStyles,
} from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { Dialog } from '~/components/dialog';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { sortIncentives } from '~/utils/incentives';
import { PriceSection } from '../components/price-section';
import { RateSection } from '../components/rate-section';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';

export function PriceDetailsDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		open &&
			appTrackingService.viewChangeEvent({
				eventName: 'configurator - price details layer',
				componentName: 'price-details-layer',
			});
	}, [open]);
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDetailsDialogContent />
		</Dialog>
	);
}

function PriceDetailsDialogContent() {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { priceDateRemark } = usePriceDetailRemarks(config.useStaticPriceDateRemark);
	const { getReference } = useFootnotes();
	let product: string;
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);
	const { formatter } = useMarketConfig();
	const { showIframeDialog } = useIframeDialog();

	if (config.showCaESummaryText && !config.hideConsumptionEmissionEngineName) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const incentives =
		config.enableIncentives && carlineData.carline?.configuredCar.incentives
			? sortIncentives(carlineData.carline?.configuredCar.incentives)
			: [];
	const selectedIncentives = incentives.filter((incentive) => incentive.status.selected);

	const priceParts = getFilteredPriceParts(priceSummary, config);

	if (selectedProductSettings) {
		product = selectedProductSettings['@ID'] ?? '';
	} else {
		product = defaultProduct ?? '';
	}

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'bold' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					{!config.hideConsumptionEmissionEngineName && (
						<FootnoteReferenceNgc reference={getReference(CAE_CONFIGUREDCAR_REMARK)} />
					)}
				</Headline>
			</div>

			<div className="flex flex-col">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			<ul className="flex flex-col gap-6">
				{priceParts?.map((part) => (
					<PriceSection
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						disclaimer={null}
					/>
				))}
			</ul>
			<Divider />

			{config.enableIncentives && selectedIncentives.length > 0 && (
				<>
					<SubtotalStyles>
						<PriceSection
							pricePartType="subtotal"
							title={i18n('ngc.currency.subtotal')}
							subtitle={
								<Layout
									as={'label'}
									columnGap="8px"
									margin={['4px', '0', '0', '0']}
									align={'center'}
									className="subtitle">
									<Icon name={'info-circle'} size={'xs'} />
									<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
										{i18n('ngc.priceDetails.subtotal.tooltip')}
									</Text>
								</Layout>
							}
							price={formatCurrency(
								priceSummary?.parts?.find((part) => part.type === 'totalWithoutIncentives')?.price,
								vatConfig.totalPrice,
							)}
							disclaimer={null}
						/>
						{selectedIncentives.map((incentive) => {
							return (
								<PriceSection
									key={incentive.id}
									pricePartType="incentive"
									title={incentive.action.title}
									price={formatter.formatPrice(incentive.price.value)}
									disclaimer={null}
								/>
							);
						})}
					</SubtotalStyles>
					<Divider />
				</>
			)}

			<PriceSection
				pricePartType="total"
				title={i18n('ngc.priceDetails.total')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			{catalog?.priceSummary?.monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<RateSectionContainerStyles>
					<RateSection
						pricePartType="totalRate"
						title={
							financialProducts.find((prod) => {
								return prod.key === product;
							})?.val ?? i18n('ngc.priceDetails.privateLeasing')
						}
						price={formatMonthlyRate(catalog.priceSummary?.monthlyRate)}
						bold
					/>
					<TextLink
						onClick={() => {
							closeDialog('priceDetails');
							appTrackingService.summaryClickEvent({
								eventAction: EventAction.InternalLink,
								eventName: `configurator (price details layer) - click to open financing options layer`,
								componentName: 'price-details-layer',
								label: i18n('ngc.actions.changeLeasingRate'),
							});
							openDialog('monthlyPriceRatesFsag');
						}}
						size="sm"
						variant="standalone">
						{i18n('ngc.actions.changeLeasingRate')}
					</TextLink>
					{config.deliveryCostUrl && (
						<TextLink
							onClick={
								config.useIframe
									? () =>
											showIframeDialog({
												url: config.deliveryCostUrl,
												name: 'delivery-cost-layer',
											})
									: () => window.open(config.specialOfferUrl)
							}
							size="sm"
							variant="standalone">
							{i18n('ngc.priceDetails.deliveryCosts')}
						</TextLink>
					)}
				</RateSectionContainerStyles>
			)}
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
		</div>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
export function usePriceDetailRemarks(staticRemark: boolean): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n } = useI18n();
	const { priceSummary } = useCatalog();
	return {
		priceDateRemark: staticRemark ? i18n('ngc.summary.remark') : priceSummary?.priceDate?.text,
	};
}
